import { config } from '../../../../../config';
import DigiobcanApi, {
  type AdminReportDetail,
} from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import { useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import UndoIcon from '@mui/icons-material/Undo';
import { useAuthRoles } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import { formatDate } from 'date-fns';
import React from 'react';

type Props = {
  report: AdminReportDetail;
  /** Obnova obálky aj nálezov - archivácia mení celú obrazovku. */
  onDone: () => Promise<unknown>;
  /**
   * `banner` je pás nad obálkou pri archivovanom podnete, `action` je tlačidlo
   * „Archivovať" na konci obálky. Sú to dva pohľady na jednu vec, preto jeden
   * komponent: mutácia, hlásenia aj logovanie sú spoločné a dve kópie by sa
   * rozišli práve v textoch, ktoré tu nesú význam.
   */
  variant: 'banner' | 'action';
  /**
   * Po archivácii sa z detailu odchádza - podnet by sa stal 404 pre každého,
   * kto nemá `pruser:reports:archived:read`. Kam, rozhoduje stránka: nesie si
   * `search` zoznamu, ktorý komponent nepozná.
   */
  onArchived: () => void;
  /**
   * Úradník podnet len vidí - nemá kategóriu, v ktorej by ho riešil
   * (`pruser:report-categories:read-all`). Archivácia je zápis nad podnetom,
   * takže ju API odmietne 403; tlačidlo „Archivovať" ani „Obnoviť podnet" tu
   * preto nemajú čo robiť. Pás so stavom zostáva - ten nič nemení.
   */
  readOnly?: boolean;
};

/**
 * Archivácia podnetu a jej vrátenie.
 *
 * **Za potvrdzovacím dialógom, nie ako prepínač.** Vedľajší
 * [[ReportVisibilityToggle]] dialóg zámerne nemá - tam je to stav, ktorý treba
 * vidieť, a vracia sa tým istým klikom. Tu podnet z obrazovky zmizne a
 * úradníkovi bez `archived:read` sa k nemu už nedostane, takže to trenie
 * dialóg má mať.
 *
 * **Tvarom aj miestom je to dvojča archivácie hlasovania**
 * ([[VotingAdminSummary]]): za linkou na spodku karty, nadpis, krátke
 * vysvetlenie dôsledku a červené tlačidlo `Archivovať`. Sú to dve akcie tej
 * istej triedy - obe vyradia záznam z aplikácie a obe stoja mimo bežného
 * životného cyklu - takže nemajú vyzerať ako dve rôzne veci.
 *
 * V hlavičke stránky teda nie je: tá patrí tomu, čo o podnete platí (lehota,
 * hlasy, „nevhodné"), nie kroku, ktorý podnet odstráni.
 *
 * Jediná vec, ktorou sa od hlasovania líši, je **vratnosť**: archivácia
 * hlasovania je GDPR zabudnutie a jej vysvetlenie končí vetou „Vrátiť sa to
 * nedá", kým archivácia podnetu sa vrátiť dá. Sľúbiť tu nevratnosť by bola
 * nepravda, tak na tom mieste stojí to, čo pre daného úradníka naozaj platí -
 * a pre toho bez `archived:read` je to práve tá nepríjemná časť.
 *
 * Text dialógu hovorí **dôsledok**, nie „ste si istý": čo sa stane a komu.
 * A pri úradníkovi bez `archived:read` k tomu vetu navyše - že podnet po
 * archivácii neuvidí ani on. To je jediná časť tohto rozhodnutia, ktorá sa
 * nedá vrátiť vlastnými silami.
 */
const ReportArchive: React.FC<Props> = ({
  report,
  onDone,
  variant,
  onArchived,
  readOnly = false,
}) => {
  const { logger } = useLogger();
  const { hasPermission } = useAuthRoles();
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const archived = !!report.archived_at;

  const canArchive = React.useMemo(
    () =>
      !readOnly &&
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:archive',
      }),
    [hasPermission, readOnly],
  );

  const canReadArchived = React.useMemo(
    () =>
      hasPermission({
        client_id: config.CLIENT_ID,
        permission: 'pruser:reports:archived:read',
      }),
    [hasPermission],
  );

  const mutation = useM(
    async (archive: boolean) => {
      if (archive) {
        await DigiobcanApi.adminArchiveReport(report.id);
      } else {
        await DigiobcanApi.adminRestoreReport(report.id);
      }
      return archive;
    },
    {
      onSuccess: async (archive) => {
        toastSuccess(
          archive ? __('Podnet bol archivovaný.') : __('Podnet bol obnovený.'),
        );

        if (archive) {
          // Odchod zo stránky, nie `refetchAll`: detail archivovaného podnetu
          // je pre úradníka bez `archived:read` 404 a aj s ním je to už len
          // archív, nie rozpracovaná vec.
          onArchived();
          return;
        }

        await onDone();
      },
      onError: (error, archive) => {
        logger.error('Failed to change report archive state', {
          action: archive ? 'archiveReport' : 'restoreReport',
          error,
          data: { reportId: report.id },
        });
        toastError(
          archive
            ? __('Podnet sa nepodarilo archivovať.')
            : __('Podnet sa nepodarilo obnoviť.'),
        );
      },
    },
  );

  const run = (archive: boolean) => {
    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }
    mutation.mutate(archive);
  };

  // Bez oprávnenia nie je čo kresliť. Pás pri archivovanom podnete je výnimka:
  // ten hlási stav, nie akciu, takže patrí každému, kto sa na archivovaný
  // podnet vôbec dostal - a to je práve držiteľ `archived:read`.
  if (variant === 'action' && (archived || !canArchive)) return null;
  if (variant === 'banner' && !archived) return null;

  if (variant === 'banner') {
    return (
      <>
        <Stack
          direction='row'
          alignItems='center'
          gap={1.25}
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            // Neutrálna tmavá plocha, nie žltá: žltá v tejto obrazovke patrí
            // lehote a označeniu „nevhodné", ktoré si žiadajú zásah. Archív
            // si zásah nežiada - je to uzavretý stav a má sa tak aj čítať.
            backgroundColor: 'text.primary',
            color: 'common.white',
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              flex: 'none',
              display: 'grid',
              placeItems: 'center',
              borderRadius: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.16)',
            }}
          >
            <InventoryIcon sx={{ fontSize: 17 }} />
          </Box>

          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' fontWeight={700}>
              {__('Archivovaný podnet')}
            </Typography>
            <Typography variant='caption' sx={{ opacity: 0.85 }}>
              {report.archived_at
                ? `${__('Archivované')} ${formatDate(
                    new Date(report.archived_at),
                    'dd.MM.yyyy HH:mm',
                  )} · ${__('nevidí ho občan ani autor podnetu')}`
                : __('Nevidí ho občan ani autor podnetu.')}
            </Typography>
          </Stack>

          {canArchive && (
            <Button
              variant='outline'
              size='small'
              endIcon={<UndoIcon fontSize='small' />}
              disabled={mutation.isPending}
              onClick={() => run(false)}
              // Farby nechávame na variante: `outline` má v ui-react vlastné
              // svetlé pozadie, takže biely text z pásu by na ňom zmizol.
              sx={{ flex: 'none' }}
            >
              {__('Obnoviť podnet')}
            </Button>
          )}
        </Stack>
      </>
    );
  }

  return (
    <>
      {/* Presne ten istý tvar, aký má archivácia hlasovania na spodku svojho
          ľavého pásu ([[VotingAdminSummary]]): za linkou, nadpis, krátke
          vysvetlenie dôsledku a červené tlačidlo. Sú to dve akcie tej istej
          triedy - obe vyradia záznam z aplikácie a obe stoja mimo bežného
          životného cyklu - takže nemajú vyzerať ako dve rôzne veci.

          Oddeľovač si nesie akcia sama, nie obálka: komponent sa pri
          archivovanom podnete a pri chýbajúcom oprávnení nevykreslí vôbec
          a v karte by inak visela prázdna linka. */}
      <Divider />
      <Stack gap={0.75} alignItems='flex-start'>
        <Typography
          component='b'
          sx={{ fontSize: 13, fontWeight: 700, color: 'primary.extraDark' }}
        >
          {__('Archivovať podnet')}
        </Typography>
        {/* Vysvetlenie je krátke, plné znenie nesie potvrdenie. Na rozdiel od
            hlasovania tu **nekončí** vetou „Vrátiť sa to nedá" - archivácia
            podnetu vratná je a sľúbiť tu nevratnosť by bola nepravda.
            Namiesto toho nesie tú vetu, ktorá pre daného úradníka naozaj
            platí: kto archív nevidí, ten podnet sám späť nedostane. */}
        <Typography variant='caption' color='text.secondary'>
          {canReadArchived
            ? __(
                'Zmizne z mapy, z verejného zoznamu aj z „mojich podnetov" jeho autora. V databáze zostane a dá sa obnoviť z archívu.',
              )
            : __(
                'Zmizne z mapy, z verejného zoznamu aj z „mojich podnetov" jeho autora. V databáze zostane, ale vy ho už neuvidíte ani neobnovíte - nemáte oprávnenie na archív.',
              )}
        </Typography>
        <Button
          variant='danger'
          size='small'
          disabled={mutation.isPending}
          onClick={() => setConfirmOpen(true)}
        >
          {__('Archivovať')}
        </Button>
      </Stack>

      <ConfirmationDialog
        open={confirmOpen}
        title={__('Archivovať podnet?')}
        description={[
          __(
            'Podnet zmizne z aplikácie - z mapy, z verejného zoznamu aj z „mojich podnetov" jeho autora. V databáze zostane a nezmaže sa nič.',
          ),
          canReadArchived
            ? __(
                'Nájdete ho v zozname podnetov pod filtrom „Archivované" a odtiaľ sa dá obnoviť.',
              )
            : __(
                'Obnoviť ho môže len ten, kto má oprávnenie na archív - vy ho už neuvidíte.',
              ),
          __('Naozaj chcete pokračovať?'),
        ].join(' ')}
        confirmLabel={__('Archivovať podnet')}
        isLoading={mutation.isPending}
        onConfirm={async () => {
          run(true);
          setConfirmOpen(false);
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default ReportArchive;
